import React, { useEffect } from "react";
import "./TermsPage.css";

export default function TermsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="terms-container">
      <section className="terms-card">
        <h1 className="terms-title">TERMS &amp; CONDITIONS</h1>
        <p><strong>Effective Date:</strong> April 2026</p>
        <p>
          <strong>Company:</strong> Telome LLC, based in Connecticut
          (&ldquo;Telome,&rdquo; &ldquo;Company,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or
          &ldquo;our&rdquo;)
        </p>
        <p>
          These Terms &amp; Conditions (&ldquo;Terms&rdquo;) govern your access to and use of
          Telome&rsquo;s websites, mobile apps, software, AI-powered conversation tools,
          recordings, transcripts, feedback tools, training plans, and related products and
          services (collectively, the &ldquo;Services&rdquo;).
        </p>
        <p>
          By creating an account, accessing, or using the Services, you agree to be bound by
          these Terms. If you do not agree, do not use the Services.
        </p>

        <h2>1. Definitions</h2>
        <p>
          &ldquo;Services&rdquo; means Telome LLC&rsquo;s websites, apps, software, AI meeting and
          skill-building tools, recordings, transcripts, feedback systems, training plans,
          support services, and related functionality, whether generally available or provided
          in testing, beta, preview, or pre-release form.
        </p>
        <p>
          &ldquo;Output&rdquo; means any content generated or assisted by AI-powered features,
          including transcripts, responses, summaries, feedback, ratings, highlights, and
          training suggestions.
        </p>
        <p>
          &ldquo;Confidential Information&rdquo; means any non-public information regarding the
          Services, features, designs, documentation, roadmaps, metrics, models, and related
          materials.
        </p>

        <h2>2. Products and Services</h2>
        <p>
          Telome provides AI-powered conversation practice and communication training tools.
          Depending on the product or feature you use, the Services may allow you to generate
          and simulate sessions, customize scenarios and goals, upload materials, record
          sessions, receive feedback on strengths and areas for improvement, and obtain
          personalized training plans consisting of additional AI-driven practice sessions.
        </p>
        <p>
          Some features may be experimental, beta, preview, or pre-release. Functionality may
          be incomplete, modified, suspended, or discontinued at any time without notice. The
          Services are provided for practice, coaching, experimentation, and skill-building
          purposes only, and not as professional, legal, medical, mental health, educational,
          employment, or safety-critical advice.
        </p>

        <h2>3. Subscriptions and Purchases</h2>
        <p>
          Telome offers optional paid subscription plans that provide access to premium
          features such as additional practice time, practice meetings, training plans, and
          other subscription-only functionality. Available plans, features, billing periods,
          pricing, taxes, and promotions may vary by platform, region, and time, and are shown
          before purchase in the applicable purchase flow.
        </p>
        <p>
          Mobile in-app purchases are processed through the Apple App Store or Google Play,
          with RevenueCat helping manage subscription status and entitlements. Web purchases
          are processed through Stripe. Telome does not directly process or store your full
          payment card, bank account, or app store payment credentials.
        </p>
        <p>
          Subscriptions are billed in advance and automatically renew at the end of each
          billing period unless canceled through the platform where the subscription was
          purchased. App Store and Google Play subscriptions are managed through your app store
          account settings. Web subscriptions are managed through Telome&rsquo;s web billing flow
          powered by Stripe. Renewal charges apply at the then-current rate shown by the
          applicable store or checkout provider.
        </p>
        <p>
          If you cancel a subscription, you may continue to access the paid features until the
          end of the current billing period unless otherwise stated by the applicable store,
          checkout provider, or law. Refunds, billing disputes, subscription changes, and
          cancellations may be subject to the terms and policies of Apple, Google, Stripe, or
          the payment provider used for your purchase.
        </p>

        <h2>4. Eligibility; Registration and Access</h2>
        <p>
          You must be at least 18 years old, or at least the minimum age required in your
          country or jurisdiction to validly consent to use the Services. If you are under 18,
          you may use the Services only with the permission and supervision of your parent or
          legal guardian.
        </p>
        <p>
          To register for and use the Services, you must provide accurate, complete, and
          current account information. You may not share your login credentials, allow anyone
          else to access your account, or otherwise make your account available to another
          person. You are responsible for safeguarding your account and for all activity that
          occurs under it.
        </p>
        <p>
          If you create an account, access the Services, or use the Services on behalf of any
          other person, business, or organization, you represent and warrant that you have the
          authority to accept these Terms on their behalf and to bind them to these Terms.
        </p>

        <h2>5. Limited License</h2>
        <p>
          Subject to your compliance with these Terms, Telome grants you a limited, revocable,
          non-exclusive, non-transferable, non-sublicensable license to access and use the
          Services for your internal personal or authorized business use. All rights not
          expressly granted are reserved by Telome.
        </p>

        <h2>6. Restrictions and Acceptable Use</h2>
        <ul>
          <li>
            Copy, modify, translate, adapt, create derivative works of, reverse engineer, or
            otherwise attempt to extract source code, models, prompts, or underlying logic from
            the Services.
          </li>
          <li>
            Share, sell, lend, lease, assign, transfer, publish, sublicense, or make the
            Services or your access credentials available to unauthorized third parties.
          </li>
          <li>
            Bypass or circumvent security controls, entitlement limits, account restrictions,
            payment restrictions, or usage limitations.
          </li>
          <li>
            Use the Services for unlawful, harmful, abusive, harassing, infringing, deceptive,
            defamatory, threatening, discriminatory, or fraudulent conduct.
          </li>
          <li>
            Upload or transmit malicious code, harmful payloads, or content that violates law
            or the rights of others.
          </li>
          <li>
            Use the Services in any way that could harm yourself, other people, property,
            systems, or the public, or that encourages or facilitates harm by others.
          </li>
        </ul>

        <h2>7. Confidentiality; NDA</h2>
        <p>
          You agree to maintain the confidentiality of all Confidential Information and to use
          it solely as permitted under these Terms. You may not disclose, publish, or otherwise
          make available Confidential Information without Telome&rsquo;s prior written consent.
          You will implement reasonable safeguards to prevent unauthorized use or disclosure.
          These obligations survive termination.
        </p>

        <h2>8. Feedback; Assignment</h2>
        <p>
          You may provide feedback, bug reports, suggestions, ideas, and other comments
          regarding the Services. You hereby assign to Telome all right, title, and interest in
          and to that feedback, including all related intellectual property rights, without
          compensation, attribution, or further approval.
        </p>

        <h2>9. Privacy Notice</h2>
        <p>
          Our standalone Privacy Policy contains the primary description of how Telome
          collects, uses, stores, and shares data. You should review it together with these
          Terms.
        </p>

        <h2>10. Data Collection and Privacy</h2>
        <p>
          The Services may collect and process account and profile data such as your name,
          email address, login credentials, authentication provider information, and account
          settings. We may also collect the inputs you provide while using the Services,
          including prompts, meeting context, goals, notes, responses, uploaded materials, and
          other content you choose to submit.
        </p>
        <p>
          If you create, upload, or participate in recording sessions, we may collect and
          process recordings, transcripts, summaries, feedback, session metadata, and related
          usage information to provide the Services, support account functionality, troubleshoot
          problems, improve product performance, and develop new features. We may also collect
          usage analytics, device information, performance metrics, and crash logs for
          debugging, security, and service improvement.
        </p>
        <p>
          Telome does not sell your personal data for third-party marketing. We may use
          third-party infrastructure, cloud providers, analytics tools, communications
          services, and model providers to operate the Services. Those providers operate under
          their own terms and security practices, and your use of the Services constitutes your
          acknowledgment of the collection, use, storage, and processing of data as described
          in these Terms and in our Privacy Policy.
        </p>

        <h2>11. AI Output Disclaimer</h2>
        <p>
          Although Telome strives to improve the usefulness of AI-powered features, Output may
          vary in quality and accuracy and may be incomplete, incorrect, misleading, biased,
          offensive, or inappropriate. Telome explicitly disclaims any representation or
          warranty regarding the accuracy, truthfulness, completeness, reliability,
          practicability, or non-infringement of any Output.
        </p>
        <p>
          You must not rely on Output as factual truth or as a substitute for professional,
          medical, legal, educational, employment, career, safety, or mental health advice.
          You are solely responsible for reviewing and evaluating Output for accuracy,
          appropriateness, legality, and fitness for your use case before relying on, sharing,
          acting on, or distributing it.
        </p>

        <h2>12. Assumption of Risk; User Responsibility</h2>
        <p>
          You acknowledge that use of the Services involves inherent risks, including errors,
          bugs, service interruptions, inaccurate Output, incomplete feedback, failed uploads,
          lost content, or unexpected system behavior. You voluntarily assume all risks arising
          from your access to and use of the Services.
        </p>
        <p>
          You are solely responsible for your conduct, your decisions, your reliance on any
          Output, and any consequences arising from your use of the Services. If you use the
          Services in a manner that causes harm to yourself, to another person, to property, or
          to any business, school, team, or organization, you—not Telome—are solely
          responsible for that conduct and its consequences. Telome LLC will not be responsible
          or liable for any injury, damage, loss, claim, dispute, or harm arising out of your
          actions, your omissions, your content, your communications, your decisions, or your
          misuse of the Services.
        </p>

        <h2>13. Warranties and Disclaimers</h2>
        <p>
          THE PRODUCTS, SERVICES, AND ALL MATERIALS AND CONTENT AVAILABLE THROUGH THE PRODUCTS
          AND SERVICES ARE PROVIDED &ldquo;AS IS&rdquo; AND ON AN &ldquo;AS AVAILABLE&rdquo; BASIS.
          TO THE FULLEST EXTENT PERMITTED BY LAW, TELOME DISCLAIMS ALL WARRANTIES OF ANY KIND,
          WHETHER EXPRESS, IMPLIED, STATUTORY, OR OTHERWISE, INCLUDING: (A) ANY IMPLIED
          WARRANTY OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, QUIET
          ENJOYMENT, OR NON-INFRINGEMENT; AND (B) ANY WARRANTY ARISING OUT OF COURSE OF
          DEALING, USAGE, OR TRADE.
        </p>
        <p>
          TELOME DOES NOT WARRANT THAT THE PRODUCTS OR SERVICES, OR ANY PORTION OF THEM, OR ANY
          MATERIALS OR CONTENT AVAILABLE THROUGH THEM, WILL BE UNINTERRUPTED, ACCURATE,
          COMPLETE, SECURE, RELIABLE, ERROR-FREE, OR FREE OF VIRUSES OR OTHER HARMFUL
          COMPONENTS, AND TELOME DOES NOT WARRANT THAT ANY OF THOSE ISSUES WILL BE CORRECTED.
        </p>
        <p>
          NO ADVICE OR INFORMATION, WHETHER ORAL OR WRITTEN, OBTAINED BY YOU FROM TELOME, THE
          SERVICES, OR ANY MATERIALS OR CONTENT AVAILABLE THROUGH THE SERVICES WILL CREATE ANY
          WARRANTY THAT IS NOT EXPRESSLY STATED IN THESE TERMS. YOU UNDERSTAND AND AGREE THAT
          YOU USE ANY PORTION OF THE PRODUCTS OR SERVICES AT YOUR OWN DISCRETION AND RISK, AND
          THAT TELOME IS NOT RESPONSIBLE FOR ANY DAMAGE TO YOUR PROPERTY, DEVICES, SYSTEMS,
          DATA, RECORDINGS, FILES, OR CONTENT RESULTING FROM YOUR USE OF THE SERVICES.
        </p>
        <p>
          APPLICABLE LAW IN SOME JURISDICTIONS MAY IMPLY WARRANTIES, CONDITIONS, OR GUARANTEES
          OR IMPOSE OBLIGATIONS THAT PROVIDE YOU WITH CERTAIN CONSUMER RIGHTS. THESE TERMS ARE
          NOT INTENDED TO RESTRICT THOSE RIGHTS, AND TELOME DOES NOT DISCLAIM ANY WARRANTY OR
          OTHER RIGHT THAT TELOME IS PROHIBITED FROM DISCLAIMING UNDER APPLICABLE LAW.
        </p>

        <h2>14. Limitation of Liability</h2>
        <p>
          TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, NEITHER TELOME NOR ANY OF ITS
          AFFILIATES, LICENSORS, OFFICERS, EMPLOYEES, CONTRACTORS, AGENTS, OR PERSONNEL WILL BE
          LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, PUNITIVE, OR
          ENHANCED DAMAGES, OR FOR ANY LOSS OF PROFITS, REVENUE, GOODWILL, BUSINESS
          INTERRUPTION, USE, DATA, CONTENT, RECORDINGS, OR OTHER INTANGIBLE LOSSES, EVEN IF
          ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
        </p>
        <p>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, TELOME SHALL NOT BE LIABLE FOR ANY CLAIMS,
          LOSSES, COSTS, OR DAMAGES ARISING OUT OF OR RELATED TO YOUR USE OF OR INABILITY TO
          USE THE SERVICES, YOUR RELIANCE ON OUTPUT, YOUR INTERACTIONS WITH OTHER PEOPLE OR
          THIRD PARTIES, YOUR CONTENT, OR ANY HARM OR ALLEGED HARM TO YOURSELF OR OTHERS
          ARISING FROM YOUR USE OF THE SERVICES.
        </p>

        <h2>15. Indemnification</h2>
        <p>
          You agree to defend, indemnify, and hold harmless Telome LLC and its officers,
          employees, contractors, affiliates, licensors, and personnel from and against any
          claims, liabilities, damages, losses, judgments, costs, and expenses (including
          reasonable attorneys&rsquo; fees) arising out of or relating to: (a) your use of the
          Services; (b) your content or conduct; (c) your breach of these Terms; or (d) your
          violation of law or third-party rights.
        </p>
        <p>
          If you are a business or organization, then to the extent permitted by law, you will
          indemnify and hold harmless Telome LLC, its affiliates, and its personnel from and
          against any costs, losses, liabilities, and expenses (including attorneys&rsquo; fees)
          arising out of or relating to third-party claims concerning your use of the Services
          or Content, or your violation of these Terms.
        </p>

        <h2>16. Intellectual Property; Non-Competition; Injunctive Relief</h2>
        <p>
          The Services, including their concepts, algorithms, prompts, designs, UX, data
          models, architecture, documentation, features, and branding, are the exclusive
          property of Telome and are protected by intellectual property laws. No rights are
          granted by implication, waiver, or estoppel.
        </p>
        <p>
          You may not copy, reproduce, reverse engineer, decompile, disassemble, derive source
          code from, create derivative works of, or develop competing technology or
          applications based on or derived from access to the Services or Confidential
          Information.
        </p>
        <p>
          Any breach of this Section will cause irreparable harm for which monetary damages may
          be inadequate. Telome is entitled to immediate injunctive relief, in addition to all
          other remedies available at law or equity.
        </p>

        <h2>17. Third-Party Services and Infrastructure</h2>
        <p>
          The Services may interoperate with or rely on third-party services, APIs, cloud
          hosting, storage, networking, authentication, analytics, payment processors, and
          communications providers. Such third parties are independent providers with their own
          terms and policies, and Telome is not responsible for their acts, omissions, outages,
          security incidents, or service changes.
        </p>

        <h2>18. Compliance with Laws; Export</h2>
        <p>
          You will comply with all applicable laws, rules, and regulations in connection with
          your use of the Services, including export control and sanctions laws. You represent
          that you are not located in, under the control of, or a national or resident of any
          embargoed country or prohibited party list, and that you will not use the Services in
          violation of applicable law.
        </p>

        <h2>19. Term; Suspension; Termination; Effect</h2>
        <p>
          These Terms remain effective until terminated. Telome may suspend, restrict, or
          terminate your access at any time, with or without notice, for any reason, including
          security, legal, operational, billing, product, or policy reasons. You may stop using
          the Services at any time and may request account deletion subject to our retention and
          legal obligations.
        </p>
        <p>
          Upon termination, you must cease all unauthorized use of the Services and destroy any
          Confidential Information in your possession. Sections intended by their nature to
          survive termination will survive, including Sections 6 through 18 and 20 through 22.
        </p>

        <h2>20. Changes to Terms, Policies, and Services</h2>
        <p>
          We reserve the right to change or update these Terms, or any other of our policies or
          practices, at any time. We may also update, suspend, limit, remove, or discontinue
          any portion of the Services at any time, with or without notice.
        </p>
        <p>
          Material changes may be indicated by updating the Effective Date, posting revised
          terms on our website, or providing in-app or email notice. Your continued use of the
          Services after any change becomes effective constitutes your acceptance of the updated
          Terms.
        </p>

        <h2>21. Notices; Contact</h2>
        <p>
          Notices to Telome should be sent to support@telome.ai (or any other address we
          designate). We may provide notices to you through the Services, by email, or by
          posting on our website or within the product.
        </p>

        <h2>22. Class Action Waiver; Jury Trial Waiver</h2>
        <p>
          To the maximum extent permitted by law, you and Telome agree that any disputes will
          be conducted only on an individual basis and not in a class, collective,
          consolidated, or representative action. You and Telome waive any right to a jury
          trial to the extent permitted by law.
        </p>

        <h2>23. Governing Law; Venue</h2>
        <p>
          These Terms are governed by the laws of the State of Connecticut, without regard to
          conflicts of law principles. The exclusive venue for any dispute arising out of or
          relating to these Terms or the Services shall be the state or federal courts located
          in Connecticut, and you consent to personal jurisdiction there.
        </p>

        <h2>24. Miscellaneous</h2>
        <p>
          <strong>Severability:</strong> If any provision is held unenforceable, the remaining
          provisions will remain in full force and effect.
        </p>
        <p>
          <strong>Assignment:</strong> You may not assign or transfer these Terms without
          Telome&rsquo;s prior written consent. Telome may assign these Terms freely.
        </p>
        <p>
          <strong>Force Majeure:</strong> Telome shall not be liable for delays or failures
          resulting from events beyond its reasonable control.
        </p>
        <p>
          <strong>No Waiver:</strong> Failure to enforce any provision shall not constitute a
          waiver.
        </p>
        <p>
          <strong>Headings:</strong> Headings are for convenience only and do not affect
          interpretation.
        </p>
        <p>
          <strong>Entire Agreement:</strong> These Terms constitute the entire agreement between
          you and Telome regarding the Services and supersede all prior or contemporaneous
          understandings relating to them.
        </p>
      </section>
    </main>
  );
}
